<template>
  <v-container class="fill-height d-flex flex-column ma-0 pa-0 bg-white" fluid>
    <!-- Header -->
    <v-sheet class="w-100 d-flex custom-header flex-column" elevation="1" style="height: 13vh">
      <v-row class="align-center fill-height ma-0">
        <v-col cols="auto" class="d-flex align-center justify-start px-6 pt-2">
          <img :src="logo" :alt="$t('alt.logo')" height="45" style="object-fit: cover" />
        </v-col>
        <v-col class="h-100 d-flex align-self-center pa-0 justify-center">
          <stepper></stepper>
        </v-col>
        <v-col cols="auto" class="text-end">
          <UserBlock></UserBlock>
        </v-col>
      </v-row>
    </v-sheet>

    <!-- Main Content -->
    <v-row
      class="flex-grow-1 ma-0 pa-0 w-100 overflow-hidden"
      v-if="
        typeof $route.name === 'string' &&
        ['Validate', 'Download', 'Mode', 'Convert'].includes($route.name)
      "
      style="height: calc(100vh - 23vh)"
    >
      <v-col cols="6" class="h-100">
        <router-view></router-view>
      </v-col>
      <v-col cols="6" class="align-self-center h-100">
        <v-row class="ma-0 bg-white w-100 h-100">
          <v-col
            cols="12"
            class="d-flex align-center justify-center"
            :class="{
              'h-100':
                $route.name === 'Validate' ||
                $route.name === 'Download' ||
                $route.name === 'Convert',
            }"
          >
            <img
              v-if="
                $route.name === 'Validate' ||
                $route.name === 'Download' ||
                $route.name === 'Convert'
              "
              :src="sidePanelSrc + '?t=' + cacheBuster"
              :alt="$t('alt.logo')"
              style="object-fit: contain; max-width: 100%; max-height: 100%; height: auto"
            />

            <img v-else :src="sidePanelSrc" :alt="$t('alt.logo')" style="object-fit: contain" />
          </v-col>
        </v-row>
      </v-col>
    </v-row>

    <router-view v-else></router-view>

    <!-- Footer -->
    <custom-footer
      :showNext="footerConfig.showNext"
      :disableNext="footerConfig.disableNext"
      :loadingNext="footerConfig.loadingNext"
      @next="footerConfig.onNext"
      :showReset="footerConfig.showReset"
      :disableReset="footerConfig.disableReset"
      :loadingReset="footerConfig.loadingReset"
      @reset="footerConfig.onReset"
      :showAutoFix="footerConfig.showAutoFix"
      :disableAutoFix="footerConfig.disableAutoFix"
      :loadingAutoFix="footerConfig.loadingAutoFix"
      @autoFix="footerConfig.onAutoFix"
      :showEdit="footerConfig.showEdit"
      :loadingEdit="footerConfig.loadingEdit"
      @edit="footerConfig.onEdit"
      :showBack="footerConfig.showBack"
      :disableBack="footerConfig.disableBack"
      :showFeedback="footerConfig.showFeedback"
      :showDownload="footerConfig.showDownload"
      :showRestart="footerConfig.showRestart"
      :showGenerate="footerConfig.showGenerate"
      :disableGenerate="footerConfig.disableGenerate"
      :loadingGenerate="footerConfig.loadingGenerate"
      @generate="footerConfig.onGenerate"
      :showSelect="footerConfig.showSelect"
      :disableSelect="footerConfig.disableSelect"
      :loadingSelect="footerConfig.loadingSelect"
      @select="footerConfig.onSelect"
      :showRetry="footerConfig.showRetry"
      :disableRetry="footerConfig.disableRetry"
      @retry="footerConfig.onRetry"
    ></custom-footer>
  </v-container>
</template>

<script lang="ts" setup>
import { computed, provide, ref, reactive, watch, onMounted } from 'vue'
import { useRoute } from 'vue-router'

import Stepper from '@/components/Stepper.vue'
import CustomFooter from '@/components/CustomFooter.vue'
import UserBlock from '@/components/UserBlock.vue'

import type { FooterConfig, FooterConfigProvider } from '@/types/footerConfig'
import sidePanel from '@/assets/sidePanel.svg'
import { useUploadedCreativeStore } from '@/stores/uploadedCreative'
import { useAssetStore } from '@/stores/asset'
import appConfig from '@/constants/appConfig'


const uploadedCreativeStore = useUploadedCreativeStore()
const assetStore = useAssetStore()
const route = useRoute()


const footerConfig = reactive<FooterConfig>({
  showNext: false,
  disableNext: false,
  loadingNext: false,
  onNext: () => {},
  onReset: () => {},
})

provide<FooterConfigProvider>('footerConfig', {
  footerConfig,
  setFooterConfig: (config: Partial<FooterConfig>) => {
    Object.assign(footerConfig, config)
  },
})

const cacheBuster = ref(Date.now())
const logo = appConfig.APP_LOGO

const sidePanelSrc = computed(() => {
  if (route.name === 'Validate' || route.name === 'Convert')
    return uploadedCreativeStore.uploadedCreativeUrl
  if (route.name === 'Download') return assetStore.creativeS3Url
  return sidePanel
})

watch(
  () => route.name,
  () => {
    cacheBuster.value = Date.now() // Force cache refresh
  },
)
</script>

<style scoped>
.blurred {
  filter: blur(4px);
  transition: filter 1s ease;
  border-radius: 5%;
}
.clear {
  filter: blur(0px);
  transition: filter 1s ease;
}
img {
  display: block;
}
</style>
